import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Representation } from "./types";

export default function ProposalContent({
  representation: offer,
  title,
  locale,
  headingTag: Heading = "h1",
}: {
  representation: Representation;
  title?: string;
  locale?: string;
  headingTag?: "h1" | "h2" | "h3";
}) {
  const LineHeading = Heading === "h1" ? "h2" : Heading === "h2" ? "h3" : "h4";
  const { i18n } = useTranslation();
  const language = locale || offer.locale;
  const t = i18n.getFixedT(language);
  const money = (minor: number) =>
    new Intl.NumberFormat(language, {
      style: "currency",
      currency: offer.total.currency,
    }).format(minor / 100);
  const date = (value: string) =>
    new Intl.DateTimeFormat(language, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: offer.schedule.time_zone,
    }).format(new Date(value));
  return (
    <article data-testid="proposal-content" className="space-y-6 break-words">
      <div>
        <p className="text-muted-foreground">
          {offer.company_name} · {offer.location_name}
        </p>
        <Heading className="mt-2 text-page font-display">
          {title || t("proposals.title")}
        </Heading>
        <p className="mt-3">
          {offer.customer.name} · {offer.vehicle.make} {offer.vehicle.model}
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t("jobs.interval")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p>{offer.schedule.bay_name}</p>
          <p>
            {date(offer.schedule.starts_at)} – {date(offer.schedule.ends_at)}
          </p>
          <p className="text-muted-foreground text-sm">
            {offer.schedule.time_zone}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t("jobs.services")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y">
            {offer.total.lines.map((line) => (
              <li key={line.line_id} className="space-y-2 py-4 first:pt-0">
                <div className="flex flex-wrap justify-between gap-2">
                  <LineHeading className="font-medium">{line.name}</LineHeading>
                  <span>{money(line.total_minor)}</span>
                </div>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {line.description}
                </p>
                <p>
                  {line.quantity} × {money(line.unit_price_minor)}
                </p>
                {line.item_discount_minor > 0 && (
                  <p>
                    {t("proposals.discount")}: {money(line.item_discount_minor)}
                  </p>
                )}
                <p className="text-muted-foreground text-sm">
                  {line.tax_label || t("proposals.tax")} {line.tax_percentage}%
                  · {t(`proposals.${line.tax_mode}`)} · {money(line.tax_minor)}
                </p>
              </li>
            ))}
          </ul>
          <dl className="mt-6 space-y-2 border-t pt-4">
            {(
              [
                ["net", offer.total.net_minor],
                ["tax", offer.total.tax_minor],
                ["discount", offer.total.job_discount_minor],
                ["total", offer.total.total_minor],
              ] as const
            ).map(([label, amount]) => (
              <div className="flex justify-between gap-4" key={label}>
                <dt className={label === "total" ? "font-semibold" : ""}>
                  {t(`proposals.${label}`)}
                </dt>
                <dd className={label === "total" ? "font-semibold" : ""}>
                  {money(amount)}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </article>
  );
}
